import type { IntakeItem } from '@billing/validation/intakeItem';
import { DateTime } from 'luxon';

/**
 * "Window" in this file means an app-level named browser tab/window target.
 * Browser docs call this a "named browsing context" used by link targets/window.open.
 */
export const CLAIMS_WINDOW_TARGET = 'claims-window';
export const QUERIES_WINDOW_TARGET = 'queries-window';
export const UPLOADS_WINDOW_TARGET = 'uploads-window';

/**
 * BroadcastChannel used for quick-create coordination between Uploads and Claims tabs.
 */
export const CLAIMS_WINDOW_CHANNEL = 'claims-window-channel';

export const QUICK_CREATE_PARAM_KEYS = {
  createType: 'taskCreateClaimType',
  providerID: 'providerID',
  intakeItemID: 'intakeItemID',
  siteID: 'siteID',
  serviceDate: 'serviceDate',
} as const;

export const QUICK_CREATE_TYPES = ['claim', 'bulk', 'repeat'] as const;
export type QuickCreateType = (typeof QUICK_CREATE_TYPES)[number];

const QUICK_CREATE_TYPE_SET = new Set<string>(QUICK_CREATE_TYPES);

export const isQuickCreateType = (
  value: string | null | undefined
): value is QuickCreateType => !!value && QUICK_CREATE_TYPE_SET.has(value);

export const getQuickCreateTypeFromSearchParams = (
  searchParams: URLSearchParams
): QuickCreateType | null => {
  const createTypeParam = searchParams.get(QUICK_CREATE_PARAM_KEYS.createType);
  return isQuickCreateType(createTypeParam) ? createTypeParam : null;
};

export const buildQuickCreateHref = (
  intakeItem:
    | (Pick<IntakeItem, 'id' | 'siteID' | 'providerID'> & {
        createdAt: Date | string;
      })
    | null,
  createType: QuickCreateType
) => {
  const params = new URLSearchParams();
  params.set(QUICK_CREATE_PARAM_KEYS.createType, createType);

  if (intakeItem?.id) {
    params.set(QUICK_CREATE_PARAM_KEYS.intakeItemID, intakeItem.id);
  }
  if (intakeItem?.siteID) {
    params.set(QUICK_CREATE_PARAM_KEYS.siteID, intakeItem.siteID);
  }
  if (intakeItem?.providerID) {
    params.set(QUICK_CREATE_PARAM_KEYS.providerID, intakeItem.providerID);
  }
  if (intakeItem?.createdAt) {
    const createdAt = intakeItem.createdAt;
    const serviceDate =
      typeof createdAt === 'string'
        ? DateTime.fromISO(createdAt, { setZone: true }).toISODate()
        : DateTime.fromJSDate(createdAt, { zone: 'utc' }).toISODate();
    if (serviceDate)
      params.set(QUICK_CREATE_PARAM_KEYS.serviceDate, serviceDate);
  }

  const query = params.toString();
  return query ? `/claims?${query}` : '/claims';
};
